import _ from 'lodash';
import moment from 'moment-timezone';

import {
  percentify,
  thousandify
} from '../../../../main/scripts/utils/NumFmtUtils';

export const getBuildInfoProperties = (project, hasOutdatedScience) => {
  const sentimentProgress = project.last_build_info.sentiment?.progress ?? 0;
  const sentimentProgressText = percentify(sentimentProgress, 1, 0);

  return {
    sentimentProgress,
    sentimentProgressText,
    status: getStatus(project, hasOutdatedScience)
  };
};

const getBaseStatus = (project, hasOutdatedScience) => {
  const docCount = project.document_count;
  const buildInfo = project.last_build_info;
  const { sentiment } = buildInfo;
  const waitingOnSentiment =
    sentiment?.start_time != null && sentiment?.stop_time == null;
  const sentimentBuildFailed = sentiment?.success === false;
  const sentimentBuildCancelled = sentiment?.details?.reason === 'cancelled';
  // TODO: Clarify distinction b/w "Waiting for data" and "Some data present,
  // but not yet recalculating"
  const uploadText = 'Upload data';
  const isNewProject = project?.last_successful_build_time;
  const hasPreviousBuild = project?.last_successful_build_time != null;

  if (buildInfo.stop_time && !buildInfo.success && !hasPreviousBuild) {
    // The build failed with nothing to fall back on, so the project really
    // cannot be opened. When an earlier build did succeed the project still
    // works off that one, and the failure is reported as a warning instead —
    // see getStatus below.
    return {
      text: 'Build failed',
      message: `Error: ${buildInfo.reason}`,
      uploadText,
      isDashboardEnabled: false,
      light: 'red'
    };
  } else if (_.isEmpty(buildInfo)) {
    // no build has been attempted
    return {
      text: 'Waiting for data',
      message: 'Upload your data in order to explore it.',
      isDashboardEnabled: !isNewProject ? false : true,
      uploadText
    };
  } else if (!buildInfo.stop_time) {
    // the last build hasn't stopped yet
    return {
      text: 'Processing data',
      message: "We're currently processing recently uploaded documents.",
      uploadText: '', // TODO: Have a button that cancels the upload.
      isDashboardEnabled: false,
      light: 'yellow',
      inProgress: true
    };
  } else if (hasOutdatedScience) {
    // the project is too old to open
    return {
      text: getDocCountText(docCount),
      message: '',
      uploadText,
      isDashboardEnabled: false,
      light: 'slate'
    };
  } else if (sentimentBuildCancelled) {
    return {
      text: getDocCountText(docCount),
      message:
        'Sentiment build was cancelled. You can still explore your project.',
      uploadText,
      isDashboardEnabled: true,
      light: 'green'
    };
  } else if (sentimentBuildFailed) {
    return {
      text: getDocCountText(docCount),
      message:
        'There was a problem calculating sentiment, but you can still explore your project',
      uploadText,
      isDashboardEnabled: true,
      light: 'green'
    };
  } else {
    return {
      text: getDocCountText(docCount),
      message: waitingOnSentiment
        ? "We're still calculating sentiment, but you can explore your project in the meantime."
        : 'This project is ready to be viewed and explored.',
      uploadText,
      light: 'green',
      isDashboardEnabled: true,
      waitingOnSentiment
    };
  }
};

function defineStatus(scrapes) {
  if (!Array.isArray(scrapes) || scrapes.length === 0) return 'unknown';

  const [serta, legacy] = scrapes;

  const serverTz = moment.tz.guess();

  const toMsSerta = v => {
    if (v == null) return Number.NEGATIVE_INFINITY;
    const m = moment(v, moment.ISO_8601, true);
    return m.isValid() ? m.valueOf() : Number.NEGATIVE_INFINITY;
  };

  const toMsLegacy = v => {
    if (v == null) return Number.NEGATIVE_INFINITY;
    const m = moment.tz(
      v,
      [
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DD HH:mm:ss.SSS',
        moment.ISO_8601
      ],
      serverTz
    );
    return m.isValid() ? m.valueOf() : Number.NEGATIVE_INFINITY;
  };

  const legacyRaw = legacy.end_time ?? legacy.start_time;
  const legacyMs = toMsLegacy(legacyRaw);
  const sertaMs = toMsSerta(serta.updatedAt);

  const legacyValid = Number.isFinite(legacyMs);
  const sertaValid = Number.isFinite(sertaMs);

  if (!legacyValid && !sertaValid) {
    return 'unknown';
  }

  if (legacyValid && !sertaValid) {
    return legacy;
  }

  if (!legacyValid && sertaValid) {
    return serta;
  }

  return legacyMs >= sertaMs ? legacy : serta;
}

const getStatus = (project, hasOutdatedScience) => {
  // get base  status
  const status = getBaseStatus(project, hasOutdatedScience);
  const isNewProject = project?.last_successful_build_time;

  const scrapes = Array.isArray(project?.scrapes) ? project.scrapes : [];

  const scrapeInfo = scrapes.length
    ? scrapes.length > 1 && scrapes.some(s => s?.failedLinks)
      ? defineStatus(scrapes)
      : scrapes[0]
    : undefined;

  const scrapeRunning = scrapes.some(s => s?.status === 'running');
  const scrapeFailed =
    !scrapeRunning && scrapes.some(s => s?.status === 'failed');
  const partiallyFinished =
    !scrapeRunning && scrapes.some(s => s?.status === 'partiallyCompleted');
  const scrapeSucceeded =
    !scrapeRunning &&
    !scrapeFailed &&
    !partiallyFinished &&
    scrapes.length > 0 &&
    scrapes.every(s => s?.status === 'completed');
  // if a scrape is running or failed, override the status message and light fields
  // with scrape status details
  if (scrapeRunning) {
    status.message = 'Scraping in progress...';
    status.isDashboardEnabled = !isNewProject ? false : true;
    status.light = 'yellow';
    status.inProgress = true;
  } else if (scrapeFailed) {
    status.message =
      'LAST SCRAPE TASK FAILED: ' + scrapeInfo && scrapeInfo.status_details;
    status.light = 'red';
    status.isDashboardEnabled = !isNewProject ? false : true;
  } else if (partiallyFinished) {
    status.message =
      'Scrape completed but some urls was failed, we continue processing them, but project may rebuild several times.';
    status.isDashboardEnabled = true;
  } else if (scrapeSucceeded) {
    // if scrape succeeded, append the success information to the message but otherwise
    // keep most of the details of the legacy status
    status.message = 'Scrape completed successfully. ' + status.message;
    status.isDashboardEnabled = true;
  }

  // The project opens on its last successful build, so its status describes
  // that build. The failure since then is still worth flagging, but as a note
  // on a working project rather than as its headline state.
  const buildInfo = project.last_build_info;
  if (buildInfo.stop_time && !buildInfo.success && isNewProject) {
    status.warning = `The last build failed, so this project does not include anything uploaded since. ${buildInfo.reason}`;
  }

  return status;
};

const getDocCountText = docCount =>
  (docCount === 0 ? 'No' : thousandify(docCount)) +
  ' Document' +
  (docCount !== 1 ? 's' : '');
